import { TrackedButton } from '@automata/ui';
import {
  CheckCircleSharp,
  CloudUploadRounded,
  DeleteOutlineRounded,
  ErrorSharp,
  InsertDriveFileSharp,
  ReplayRounded,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { DragEvent, useRef } from 'react';

import { styles } from './ChunkedJsonUploader.styles';
import { DropZone } from './DropZone';
import { UploadStatusBadge } from './UploadStatusBadge';
import { useChunkedUploader } from './useChunkedUploader';

export type ChunkedJsonUploaderProps = {
  workflowInfo: { id: string; name: string };
  isEditable: boolean;
  onChange: (newJson: string) => void;
};

export const ChunkedJsonUploader = ({
  workflowInfo,
  isEditable,
  onChange,
}: ChunkedJsonUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    selectedFile,
    uploadProgress,
    status,
    error,
    handleFileChange,
    handleRetry,
    handleClear,
  } = useChunkedUploader({
    workflowInfo,
    onChange,
  });

  const openFileDialog = () => fileInputRef.current?.click();

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isEditable) return;

    const file = event.dataTransfer?.files?.[0];
    // TODO: At the moment, the download starts immediately after the file
    //  for download is selected. In the future, this functionality may be moved
    //  to the SAVE button, but for now, this button is being completely updated
    //  workflow.definition and changes are needed on BE
    if (file) void handleFileChange(file);
  };

  return (
    <Stack spacing={2} sx={styles.container}>
      <Stack direction="row" alignItems="center" gap={1}>
        <CloudUploadRounded sx={styles.header} />
        <Typography variant="body1">Upload JSON file</Typography>
      </Stack>

      {(status === 'idle' || !selectedFile) && (
        <DropZone
          isEditable={isEditable}
          fileInputRef={fileInputRef}
          onFileSelected={handleFileChange}
          onOpenFileDialog={openFileDialog}
          onDrop={handleDrop}
        />
      )}

      {selectedFile && status !== 'idle' && (
        <Stack spacing={1} sx={styles.uploadCard}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.cardHeader}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <InsertDriveFileSharp sx={styles.fileInfoText} />
              <Stack>
                <Typography fontWeight={500}>{selectedFile.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Stack>
            </Stack>
            {status === 'success' && isEditable && (
              <TrackedButton
                trackLabel="wflow.json.replace"
                fullWidth={false}
                loading={false}
                variant="contained"
                component="label"
              >
                REPLACE
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleFileChange(file);
                    e.currentTarget.value = '';
                  }}
                />
              </TrackedButton>
            )}
          </Stack>

          <Box style={{ margin: '16px' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={styles.uploadStatusRow}
            >
              <Typography variant="body2" fontWeight={500}>
                Upload status
              </Typography>
              <UploadStatusBadge status={status} />
            </Stack>

            {status === 'uploading' && (
              <LinearProgress variant="determinate" value={uploadProgress} />
            )}

            {status === 'success' && (
              <Stack spacing={1} sx={styles.successBox}>
                <Stack
                  sx={{ display: 'flex', flexDirection: 'row' }}
                  alignItems="center"
                  spacing={1}
                >
                  <CheckCircleSharp sx={styles.completeIcon} />
                  <Typography sx={{ color: '#2e7d32', fontWeight: 500, pl: 1 }}>
                    Upload completed successfully!
                  </Typography>
                </Stack>
                <Stack style={{ marginLeft: '40px' }}>
                  <a
                    href={URL.createObjectURL(
                      new Blob([selectedFile], { type: 'application/json' })
                    )}
                    download={selectedFile.name}
                    style={styles.downloadLink}
                  >
                    Download uploaded file
                  </a>
                </Stack>
              </Stack>
            )}

            {status === 'error' && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={styles.errorBox}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <ErrorSharp sx={styles.errorIcon} />
                  <Stack>
                    <Typography variant="body2" color="error">
                      Upload failed during transfer. Please try again.
                    </Typography>
                    {error && (
                      <Typography variant="caption" color="error">
                        Error: {error}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={handleRetry}
                    disabled={!isEditable}
                    aria-label="Retry upload"
                    sx={styles.actionButton}
                  >
                    <ReplayRounded sx={{ fontSize: 20, mr: 0.5 }} />
                    <Typography variant="caption" fontWeight={500}>
                      RETRY
                    </Typography>
                  </IconButton>
                  <IconButton
                    onClick={handleClear}
                    disabled={!isEditable}
                    aria-label="Delete file"
                    sx={styles.actionButton}
                  >
                    <DeleteOutlineRounded sx={{ fontSize: 20, mr: 0.5 }} />
                    <Typography variant="caption" fontWeight={500}>
                      DELETE
                    </Typography>
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
